import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SocialSetup from './SocialSetup';
import SocialAnalytics from './SocialAnalytics';

function SocialMedia() {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, posted, all
  const [selectedPost, setSelectedPost] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [imageSearchResults, setImageSearchResults] = useState(null);
  const [searchingImages, setSearchingImages] = useState(false);

  useEffect(() => {
    fetchPosts();
    fetchStats();
    fetchAccounts();
    const interval = setInterval(() => {
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const apiCall = async (url, options = {}) => {
    const token = document.cookie.match(/token=([^;]+)/)?.[1];
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Cookie: `token=${token}` } : {}),
        ...options.headers,
      },
      credentials: 'include',
    });
    return res.json();
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const statusMap = { pending: 'draft', approved: 'approved', posted: 'posted' };
      let url = '/api/social/posts';
      if (filter === 'pending') url += '?status=draft';
      else if (filter === 'approved') url += '?status=approved';
      else if (filter === 'posted') url += '?status=posted';

      const data = await apiCall(url);
      setPosts(data.posts || []);
    } catch (e) {
      console.error('Failed to fetch posts:', e);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiCall('/api/social/stats');
      setStats(data);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await apiCall('/api/social/accounts');
      setAccounts(data.accounts || []);
    } catch (e) {
      console.error('Failed to fetch accounts:', e);
    }
  };

  const handleApprove = async (id, targetPlatforms = []) => {
    try {
      await apiCall(`/api/social/posts/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ targetPlatforms }),
      });
      toast.success(`Post approved for: ${targetPlatforms.length > 0 ? targetPlatforms.join(', ') : 'default platform'}`);
      fetchPosts();
      fetchStats();
    } catch (e) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (id, reason = null, notes = '') => {
    try {
      // If reason provided, send feedback to help ContentAI learn
      if (reason) {
        await apiCall('/api/social/posts/feedback', {
          method: 'POST',
          body: JSON.stringify({ post_id: id, reason, notes }),
        });
      }
      await apiCall(`/api/social/posts/${id}/reject`, { method: 'POST' });
      toast.success(reason ? 'Post rejected — feedback recorded for ContentAI' : 'Post rejected');
      fetchPosts();
      fetchStats();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await apiCall(`/api/social/posts/${id}`, { method: 'DELETE' });
      toast.success('Post deleted');
      fetchPosts();
      fetchStats();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = async (id, postData) => {
    try {
      await apiCall(`/api/social/posts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(postData),
      });
      toast.success('Post updated');
      fetchPosts();
      fetchStats();
    } catch (e) {
      toast.error('Failed to update post');
    }
  };

  const handleCreate = async (postData) => {
    try {
      await apiCall('/api/social/posts', {
        method: 'POST',
        body: JSON.stringify(postData),
      });
      toast.success('Post created');
      setShowCreate(false);
      fetchPosts();
      fetchStats();
    } catch (e) {
      toast.error('Failed to create post');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Auto-fetch customers and use the first one (or default to 1)
      let customerId = 1;
      try {
        const custData = await apiCall('/api/customers?limit=1');
        const custList = custData.customers || custData;
        if (Array.isArray(custList) && custList.length > 0) {
          customerId = custList[0].id;
        }
      } catch(e) { /* fall back to 1 */ }

      const data = await apiCall('/api/social/generate', {
        method: 'POST',
        body: JSON.stringify({ customerId, count: 2 }),
      });
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Generated ${data.generated} draft posts`);
        fetchPosts();
        fetchStats();
      }
    } catch (e) {
      toast.error('Generation failed: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleImageSearch = async (query) => {
    if (!query) return;
    setSearchingImages(true);
    try {
      const data = await apiCall(`/api/images/search?query=${encodeURIComponent(query)}&count=6`);
      setImageSearchResults(data.images || []);
    } catch (e) {
      toast.error('Image search failed');
    } finally {
      setSearchingImages(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'posted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return '📝';
      case 'approved': return '✅';
      case 'posted': return '🚀';
      case 'rejected': return '❌';
      case 'failed': return '⚠️';
      default: return '❓';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'linkedin': return '💼';
      case 'facebook': return '📘';
      case 'x': return '𝕏';
      case 'instagram': return '📸';
      default: return '📱';
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          📱 Social Media Manager
        </h1>
        <div className="flex gap-3">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? '⏳ Generating...' : '🤖 Generate Posts'}
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            + New Post
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.total || 0}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Posts</div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats.byStatus?.draft || 0}</div>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">Pending Review</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.byStatus?.approved || 0}</div>
          <div className="text-sm text-green-700 dark:text-green-300">Approved</div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.byStatus?.posted || 0}</div>
          <div className="text-sm text-blue-700 dark:text-blue-300">Published</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {['pending', 'approved', 'posted', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
              filter === f
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {f === 'pending' ? 'Pending Review' : f}
          </button>
        ))}
      </div>

      {/* Posts List */}
      {posts.length === 0 ? (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
          {filter === 'pending' ? 'No posts pending review' :
           filter === 'approved' ? 'No approved posts waiting to publish' :
           filter === 'posted' ? 'No published posts yet' : 'No posts found'}
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
              onImageSearch={handleImageSearch}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {/* Create Post Modal */}
      {showCreate && (
        <CreatePostModal
          accounts={accounts}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}

      {/* Image Search Results Modal */}
      {imageSearchResults && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Select Image</h2>
              <button onClick={() => setImageSearchResults(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            {searchingImages ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {imageSearchResults.map((img, i) => (
                  <div key={i} className="cursor-pointer group" onClick={() => {
                    setImageSearchResults(null);
                    toast.success('Image selected (copy URL to attach to post)');
                    navigator.clipboard?.writeText(img.url);
                  }}>
                    <img src={img.thumb} alt={img.alt} className="w-full h-24 object-cover rounded-lg group-hover:opacity-80 transition-opacity" />
                    <p className="text-xs text-gray-400 mt-1 truncate">{img.photographer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Social Setup + Analytics — at bottom of Social Media page */}
      <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-8">
        <SocialSetup />
        <div className="mt-8">
          <SocialAnalytics />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PostCard Component — with platform checkboxes
// ============================================================
function PostCard({ post, onApprove, onReject, onDelete, onImageSearch, onEdit }) {
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    post.targetPlatforms ? (() => { try { return JSON.parse(post.targetPlatforms); } catch { return [post.platform]; } })() : [post.platform]
  );
  const [expanded, setExpanded] = useState(false);
  const [showRepost, setShowRepost] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [repostPlatforms, setRepostPlatforms] = useState([]);

  const platformOptions = [
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { id: 'facebook', label: 'Facebook', icon: '📘' },
    { id: 'x', label: 'X', icon: '𝕏' },
    { id: 'instagram', label: 'Instagram', icon: '📸' },
  ];

  const togglePlatform = (p) => {
    setSelectedPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'posted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return '📝';
      case 'approved': return '✅';
      case 'posted': return '🚀';
      case 'rejected': return '❌';
      case 'failed': return '⚠️';
      default: return '❓';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'linkedin': return '💼';
      case 'facebook': return '📘';
      case 'x': return '𝕏';
      case 'instagram': return '📸';
      default: return '📱';
    }
  };

  // Show approved platforms if already approved
  const approvedPlatforms = post.targetPlatforms ? (() => {
    try { return JSON.parse(post.targetPlatforms); } catch { return []; }
  })() : [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
      <div className="flex items-start p-4">
        {/* Platform icon */}
        <div className="text-3xl mr-4 mt-1">
          {getPlatformIcon(post.platform)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(post.status)}`}>
              {getStatusIcon(post.status)} {post.status}
            </span>
            {post.pillar && (
              <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                {post.pillar}
              </span>
            )}
            {post.scheduledTime && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                📅 {formatDate(post.scheduledTime)}
              </span>
            )}
          </div>

          {/* Post text */}
          <div className={expanded ? '' : 'max-h-24 overflow-hidden relative'}>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-2">
              {post.content}
            </p>
            {!expanded && post.content.length > 200 && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
            )}
          </div>
          {post.content.length > 200 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mb-2 text-sm text-blue-500 hover:underline"
            >
              {expanded ? '▲ Show less' : '▼ Read more'}
            </button>
          )}

          {/* Image preview */}
          {post.imageUrl && (
            <div className="mb-2">
              <img
                src={post.imageUrl}
                alt="Post attachment"
                className="max-h-32 rounded-lg"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}

          {/* Search image button if no image but has query */}
          {!post.imageUrl && post.imageQuery && post.status === 'draft' && (
            <button
              onClick={() => onImageSearch(post.imageQuery)}
              className="mb-2 text-sm text-blue-500 hover:underline"
            >
              🔍 Search image: "{post.imageQuery}"
            </button>
          )}

          {/* Hashtags */}
          {post.hashtags && (
            <p className="text-sm text-blue-500 dark:text-blue-400 mb-2">
              {post.hashtags}
            </p>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500 mb-2">
            <span>Created: {formatDate(post.createdAt)}</span>
            {post.postedAt && <span>Posted: {formatDate(post.postedAt)}</span>}
            {post.postUrl && (
              <a href={post.postUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                View Post →
              </a>
            )}
            {post.errorMessage && (
              <span className="text-red-500">Error: {post.errorMessage}</span>
            )}
          </div>

          {/* Platform checkboxes — only show for drafts */}
          {post.status === 'draft' && (
            <div className="flex items-center gap-3 mb-3 p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Post to:</span>
              {platformOptions.map(p => (
                <label key={p.id} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPlatforms.includes(p.id)}
                    onChange={() => togglePlatform(p.id)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {p.icon} {p.label}
                  </span>
                </label>
              ))}
            </div>
          )}

          {/* Approved platforms display */}
          {post.status === 'approved' && approvedPlatforms.length > 0 && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-green-50 dark:bg-green-900 rounded">
              <span className="text-xs font-medium text-green-700 dark:text-green-400">Approved for:</span>
              {approvedPlatforms.map(p => {
                const opt = platformOptions.find(o => o.id === p);
                return (
                  <span key={p} className="text-sm text-green-700 dark:text-green-400">
                    {opt?.icon || '📱'} {opt?.label || p}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 ml-4">
          {post.status === 'draft' && (
            <>
              <button
                onClick={() => onApprove(post.id, selectedPlatforms)}
                disabled={selectedPlatforms.length === 0}
                className="px-3 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ✓ Approve & Post
              </button>
              <button
                onClick={() => setShowReject(true)}
                className="px-3 py-1.5 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors"
              >
                ✕ Reject
              </button>
              <button
                onClick={() => setShowEdit(true)}
                className="px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
              >
                ✏️ Edit
              </button>
            </>
          )}
          {post.status === 'posted' && (
            <>
              <span className="text-sm text-green-500 mb-2">Posted ✓</span>
              {/* Show which platforms it was posted to */}
              {approvedPlatforms.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Posted to: {approvedPlatforms.map(p => {
                    const opt = platformOptions.find(o => o.id === p);
                    return opt ? `${opt.icon} ${opt.label}` : p;
                  }).join(', ')}
                </div>
              )}
              {/* Repost button */}
              <button
                onClick={() => setShowRepost(!showRepost)}
                className="px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors mb-2"
              >
                🔄 Repost to...
              </button>
              {showRepost && (
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded mb-2">
                  <div className="flex flex-col gap-1 mb-2">
                    {platformOptions
                      .filter(p => !approvedPlatforms.includes(p.id))
                      .map(p => (
                        <label key={p.id} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={repostPlatforms.includes(p.id)}
                            onChange={() => setRepostPlatforms(prev =>
                              prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                            )}
                          />
                          <span className="text-sm">{p.icon} {p.label}</span>
                        </label>
                      ))}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await fetch(`/api/social/posts/${post.id}/repost`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ targetPlatforms: repostPlatforms })
                        });
                        toast.success(`Reposted to ${repostPlatforms.join(', ')}`);
                        setShowRepost(false);
                        setRepostPlatforms([]);
                        window.location.reload();
                      } catch (e) {
                        toast.error('Repost failed');
                      }
                    }}
                    disabled={repostPlatforms.length === 0}
                    className="px-3 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
                  >
                    Send
                  </button>
                </div>
              )}
            </>
          )}
          {post.status !== 'posted' && (
            <button
              onClick={() => onDelete(post.id)}
              className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded text-sm hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
      {showEdit && (
        <EditPostModal
          post={post}
          onImageSearch={onImageSearch}
          onClose={() => setShowEdit(false)}
          onSave={(id, data) => { onEdit(id, data); setShowEdit(false); }}
        />
      )}
      {showReject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReject(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Why are you rejecting this post?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Your feedback helps ContentAI learn and improve future drafts.</p>
            <div className="space-y-2 mb-4">
              {[
                ['off_topic', 'Off-topic / irrelevant'],
                ['bad_voice', 'Wrong voice / tone'],
                ['too_promotional', 'Too promotional / salesy'],
                ['factual_error', 'Factual error'],
                ['poor_quality', 'Poor writing quality'],
                ['duplicate', 'Duplicate / too similar to recent post'],
                ['bad_hashtags', 'Bad or irrelevant hashtags'],
                ['other', 'Other'],
              ].map(([val, label]) => (
                <label key={val} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    name="rejectReason"
                    value={val}
                    checked={rejectReason === val}
                    onChange={e => setRejectReason(e.target.value)}
                  />
                  {label}
                </label>
              ))}
            </div>
            <textarea
              value={rejectNotes}
              onChange={e => setRejectNotes(e.target.value)}
              rows={3}
              placeholder="Additional notes (optional) — e.g. 'too many emojis', 'sounded like a robot', 'better for LinkedIn than X'..."
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowReject(false); setRejectReason(''); setRejectNotes(''); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!rejectReason) { toast.error('Please select a reason'); return; }
                  onReject(post.id, rejectReason, rejectNotes);
                  setShowReject(false);
                  setRejectReason('');
                  setRejectNotes('');
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
              >
                ✕ Reject & Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// EditPostModal Component — edit draft posts with image search
// ============================================================
function EditPostModal({ post, onImageSearch, onClose, onSave }) {
  const [content, setContent] = useState(post.content || '');
  const [hashtags, setHashtags] = useState(post.hashtags || '');
  const [imageUrl, setImageUrl] = useState(post.imageUrl || '');
  const [pillar, setPillar] = useState(post.pillar || '');
  const [scheduledTime, setScheduledTime] = useState(post.scheduledTime ? post.scheduledTime.substring(0, 16) : '');
  const [imageSearchQuery, setImageSearchQuery] = useState(post.imageQuery || '');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSearch = async () => {
    if (!imageSearchQuery) return;
    setSearching(true);
    try {
      const token = document.cookie.match(/token=([^;]+)/)?.[1];
      const res = await fetch(`/api/images/search?query=${encodeURIComponent(imageSearchQuery)}&count=6`, {
        credentials: 'include',
        headers: token ? { Cookie: `token=${token}` } : {},
      });
      const data = await res.json();
      setSearchResults(data.images || []);
    } catch (e) {
      toast.error('Image search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    onSave(post.id, {
      content,
      hashtags,
      imageUrl,
      pillar,
      scheduledTime: scheduledTime || null,
      status: post.status,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Edit Post</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              required
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hashtags</label>
              <input
                type="text"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="#AustinTX #SmallBusiness"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pillar</label>
              <input
                type="text"
                value={pillar}
                onChange={(e) => setPillar(e.target.value)}
                placeholder="ea-life, plain-it-tips..."
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => setImageUrl('')}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm whitespace-nowrap"
                >
                  Remove Image
                </button>
              )}
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Preview"
                className="mt-2 max-h-32 rounded-lg"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </div>

          {/* Image Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search for an Image</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={imageSearchQuery}
                onChange={(e) => setImageSearchQuery(e.target.value)}
                placeholder="Search keywords..."
                className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching || !imageSearchQuery}
                className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {searching ? '⏳' : '🔍 Search'}
              </button>
            </div>
            {searchResults && (
              <div className="mt-3 grid grid-cols-3 gap-3">
                {searchResults.map((img, i) => (
                  <div
                    key={i}
                    className="cursor-pointer group relative"
                    onClick={() => { setImageUrl(img.url); setSearchResults(null); toast.success('Image selected'); }}
                  >
                    <img
                      src={img.thumb}
                      alt={img.alt}
                      className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                    />
                    <p className="text-xs text-gray-400 mt-1 truncate">{img.photographer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schedule (optional)</label>
            <input
              type="datetime-local"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// CredentialsPanel Component — install API keys per platform
// ============================================================
function CredentialsPanel() {
  const [show, setShow] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [credFields, setCredFields] = useState({});
  const [testing, setTesting] = useState(null);

  const apiCall = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options.headers },
      credentials: 'include',
    });
    return res.json();
  };

  const fetchCreds = async () => {
    const data = await apiCall('/api/social/credentials');
    setCredentials(data.credentials || []);
  };

  useEffect(() => {
    if (show) fetchCreds();
  }, [show]);

  const handleSave = async (platform) => {
    await apiCall(`/api/social/credentials/${platform}`, {
      method: 'POST',
      body: JSON.stringify({ credentials: credFields }),
    });
    toast.success(`${platform} credentials saved`);
    setEditingPlatform(null);
    setCredFields({});
    fetchCreds();
  };

  const handleTest = async (platform) => {
    setTesting(platform);
    try {
      const data = await apiCall(`/api/social/credentials/${platform}/test`, { method: 'POST' });
      if (data.ok) {
        toast.success(`✓ ${data.message}`);
      } else {
        toast.error(`✕ ${data.error}`);
      }
    } catch (e) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const handleDelete = async (platform) => {
    if (!window.confirm(`Delete ${platform} credentials?`)) return;
    await apiCall(`/api/social/credentials/${platform}`, { method: 'DELETE' });
    toast.success(`${platform} credentials deleted`);
    fetchCreds();
  };

  // Define which fields each platform needs
  const platformFields = {
    unsplash: [{ key: 'accessKey', label: 'Access Key', placeholder: 'Unsplash Access Key' }],
    linkedin: [
      { key: 'clientId', label: 'Client ID', placeholder: 'LinkedIn App Client ID' },
      { key: 'clientSecret', label: 'Client Secret', placeholder: 'LinkedIn App Client Secret' },
      { key: 'redirectUri', label: 'Redirect URI', placeholder: 'https://ngxmc.nexgenx.org/api/social/oauth/linkedin/callback' },
    ],
    facebook: [
      { key: 'accessToken', label: 'Page Access Token', placeholder: 'Facebook Page Access Token' },
      { key: 'pageId', label: 'Page ID', placeholder: 'Facebook Page ID' },
    ],
    x: [
      { key: 'apiKey', label: 'API Key', placeholder: 'X API Key' },
      { key: 'apiSecret', label: 'API Secret', placeholder: 'X API Secret' },
      { key: 'accessToken', label: 'Access Token', placeholder: 'X Access Token' },
      { key: 'accessTokenSecret', label: 'Access Token Secret', placeholder: 'X Access Token Secret' },
    ],
    instagram: [
      { key: 'accessToken', label: 'Access Token', placeholder: 'Instagram Access Token' },
    ],
    pexels: [{ key: 'apiKey', label: 'API Key', placeholder: 'Pexels API Key' }],
  };

  const platformIcons = {
    unsplash: '📷', linkedin: '💼', facebook: '📘', x: '𝕏', instagram: '📸', pexels: '📷'
  };

  const installedPlatforms = credentials.map(c => c.platform);

  return (
    <>
      <button
        onClick={() => setShow(!show)}
        className="mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm"
      >
        🔑 Social API Credentials
      </button>

      {show && (
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-bold mb-4 text-gray-900 dark:text-gray-100">API Credentials</h2>

          {/* Installed credentials */}
          {credentials.length > 0 && (
            <div className="space-y-2 mb-6">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Installed</h3>
              {credentials.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{platformIcons[c.platform] || '📱'}</span>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">{c.platform}</div>
                      <div className="text-xs text-gray-500">
                        {c.isActive ? '✓ Active' : '✕ Inactive'} · Added {c.createdAt?.substring(0, 10)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleTest(c.platform)}
                      disabled={testing === c.platform}
                      className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      {testing === c.platform ? '⏳ Testing...' : 'Test'}
                    </button>
                    <button
                      onClick={() => { setEditingPlatform(c.platform); setCredFields({}); }}
                      className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(c.platform)}
                      className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add new credential */}
          {!editingPlatform && (
            <div>
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Install New</h3>
              <div className="flex flex-wrap gap-2">
                {Object.keys(platformFields).map(p => (
                  !installedPlatforms.includes(p) && (
                    <button
                      key={p}
                      onClick={() => { setEditingPlatform(p); setCredFields({}); }}
                      className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
                    >
                      {platformIcons[p]} {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Credential form */}
          {editingPlatform && (
            <div className="mt-4 p-4 border rounded-lg dark:border-gray-600">
              <h3 className="text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                {platformIcons[editingPlatform]} {editingPlatform.charAt(0).toUpperCase() + editingPlatform.slice(1)} Credentials
              </h3>
              {(platformFields[editingPlatform] || []).map(field => (
                <div key={field.key} className="mb-3">
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{field.label}</label>
                  <input
                    type={field.key.toLowerCase().includes('secret') || field.key.toLowerCase().includes('key') || field.key.toLowerCase().includes('token') ? 'password' : 'text'}
                    value={credFields[field.key] || ''}
                    onChange={(e) => setCredFields({ ...credFields, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  />
                </div>
              ))}
              <div className="flex gap-2">
                <button
                  onClick={() => handleSave(editingPlatform)}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditingPlatform(null); setCredFields({}); }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* LinkedIn OAuth Connect button — shows after credentials are saved */}
          {credentials.find(c => c.platform === 'linkedin') && !editingPlatform && (
            <LinkedInConnectButton credentials={credentials} />
          )}
        </div>
      )}
    </>
  );
}

// LinkedIn OAuth Connect Button
function LinkedInConnectButton({ credentials }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/social/oauth/linkedin/status', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setStatus(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [credentials]);

  const handleConnect = () => {
    window.location.href = '/api/social/oauth/linkedin/start';
  };

  if (loading) return <div className="mt-2 text-sm text-gray-500">Checking LinkedIn status...</div>;

  if (status?.connected) {
    return (
      <div className="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded-lg flex items-center justify-between">
        <div>
          <span className="text-green-700 dark:text-green-400 font-medium">✅ LinkedIn Connected</span>
          {status.profileName && <span className="ml-2 text-sm text-green-600 dark:text-green-400">as {status.profileName}</span>}
        </div>
        <button onClick={handleConnect} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
          Reconnect
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg flex items-center justify-between">
      <div>
        <span className="text-blue-700 dark:text-blue-400 font-medium">LinkedIn app credentials saved</span>
        <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
          Click to authorize and connect your LinkedIn account for posting.
        </p>
      </div>
      <button onClick={handleConnect} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
        🔗 Connect LinkedIn
      </button>
    </div>
  );
}

function CreatePostModal({ accounts, onClose, onCreate }) {
  const [content, setContent] = useState('');
  const [platform, setPlatform] = useState('linkedin');
  const [hashtags, setHashtags] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [pillar, setPillar] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate({
      content,
      platform,
      hashtags,
      imageUrl,
      pillar,
      scheduledTime: scheduledTime || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Create Post</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            >
              <option value="linkedin">LinkedIn</option>
              <option value="facebook">Facebook</option>
              <option value="x">X (Twitter)</option>
              <option value="instagram">Instagram</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              required
              placeholder="Write your post..."
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hashtags</label>
              <input
                type="text"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="#AustinTX #SmallBusiness"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pillar</label>
              <input
                type="text"
                value={pillar}
                onChange={(e) => setPillar(e.target.value)}
                placeholder="ea-life, plain-it-tips..."
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schedule (optional)</label>
            <input
              type="datetime-local"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Create Draft
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SocialMedia;
